import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { aiScenarioRepository } from "./ai-scenario.repository";
import { AiScenario } from "./entities/ai-scenario.entity";

export type CustomAIScenariosFilter = Partial<Pick<AiScenario, "userId">> & {
  scenarioId?: string;
};

const aiScenarioService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getCustomAIScenarios: builder.query<
      (AiScenario & { id: string })[],
      { where?: CustomAIScenariosFilter; offset?: string; limit?: number }
    >({
      queryFn: async ({ where = {}, offset, limit }) => {
        if (where.scenarioId) {
          const { scenarioId, ...rest } = where;
          const result = await executeQuery(() => aiScenarioRepository.findById(scenarioId));
          if ("error" in result) return { error: result.error } as any;

          const scenario = result.data as (AiScenario & { id: string }) | null;
          if (!scenario) return { data: [] } as any;

          const matches = Object.entries(rest).every(([key, value]) =>
            value == null ? true : (scenario as any)[key] === value
          );

          return { data: matches ? [scenario] : [] } as any;
        }

        const result = await executeQuery(() =>
          aiScenarioRepository.find({
            where,
            offset,
            limit,
            orderBy: ["updatedAt", "desc"],
          } as any)
        );

        if ("error" in result) return { error: result.error } as any;
        return { data: (result.data as unknown) as (AiScenario & { id: string })[] };
      },
      providesTags: ["AI_CUSTOM_SCENARIOS"],
    }),

    countCustomAIScenarios: builder.query<number, { where?: CustomAIScenariosFilter }>({
      queryFn: async ({ where = {} }) => {
        if (where.scenarioId) {
          const { scenarioId, ...rest } = where;
          const result = await executeQuery(() => aiScenarioRepository.findById(scenarioId));
          if ("error" in result) return { error: result.error } as any;

          const scenario = result.data as AiScenario | null;
          if (!scenario) return { data: 0 } as any;

          const matches = Object.entries(rest).every(([key, value]) =>
            value == null ? true : (scenario as any)[key] === value
          );

          return { data: matches ? 1 : 0 } as any;
        }

        return executeQuery(() => aiScenarioRepository.count(where as any)) as any;
      },
      providesTags: ["AI_CUSTOM_SCENARIOS"],
    }),
  }),
});

export const {
  useGetCustomAIScenariosQuery,
  useCountCustomAIScenariosQuery,
} = aiScenarioService;
